/* eslint-disable no-console */
/* eslint-disable functional/immutable-data */
"use client";

import dynamic from "next/dynamic";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";

import { supabase } from "@/lib/supabase-client";
import { type MeetingAttributes, type MeetingContent } from "@/types/meeting-page";
import { drawCityscapeIllustration } from "@/utils/design-elements";
import { getSharePageUrl } from "@/utils/share-page-url";

interface RealTimeAnnualMeetingProps {
  readonly initialData: MeetingContent;
}

interface SupabasePayload {
  readonly [key: string]: unknown;
  readonly headline?: string;
  readonly lead_paragraph?: string;
  readonly body?: string;
  readonly hero_image?: {
    readonly url: string;
    readonly width: number;
    readonly height: number;
    readonly alternativeText: string;
  };
  readonly seo?: {
    readonly description: string;
  };
}

const SharePage = dynamic(() => import("@/components/social/share-page"), { ssr: false });

const mapPayloadToContentAttributes = (payload: SupabasePayload): Partial<MeetingAttributes> => {
  const mappedData: Partial<MeetingAttributes> = {};

  if (payload.headline) mappedData.Headline = payload.headline;
  if (payload.lead_paragraph) mappedData.LeadParagraph = payload.lead_paragraph;
  if (payload.body) mappedData.Body = payload.body;

  return mappedData;
};

const updateContentFromPayload = (
  prevContent: MeetingContent,
  newData: SupabasePayload,
): MeetingContent => {
  const mappedData = mapPayloadToContentAttributes(newData);

  return {
    ...prevContent,
    data: {
      ...prevContent.data,
      attributes: {
        ...prevContent.data.attributes,
        ...mappedData,
      },
    },
  };
};

// eslint-disable-next-line max-lines-per-function
const RealTimeAnnualMeetingClient = ({
  initialData,
}: RealTimeAnnualMeetingProps): React.JSX.Element => {
  const [content, setContent] = useState<MeetingContent>(initialData);

  useEffect(() => {
    const channel = supabase
      .channel("annual_meetings")
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "annual_meetings",
        },
        (payload: { eventType: string; new: SupabasePayload }) => {
          console.log("Received payload:", payload);
          if (payload.eventType === "UPDATE" && payload.new) {
            setContent((prevContent) => updateContentFromPayload(prevContent, payload.new));
          }
        },
      )
      .subscribe();

    return (): void => {
      supabase
        .removeChannel(channel)
        .catch((err) => console.error("Failed to remove channel", err));
    };
  }, []);

  const { Headline, LeadParagraph, Body, HeroImage } = content.data.attributes;
  return (
    <div className="application-shell-cell application-shell-cell-body align-left">
      <main className="single-type">
        <div className="single-type-cell single-type-cell-content">
          <div className="level-1-heading-container-with-sharing-options">
            <div className="cell">
              <h1>{Headline}</h1>
            </div>
            <div className="cell sharing-options">
              <div className="rss-feed-with-share-page">
                <div className="rss-feed-with-share-page-cell">
                  <SharePage text={Headline} url={getSharePageUrl()} />
                </div>
              </div>
            </div>
          </div>

          <p className="lead-paragraph">{LeadParagraph}</p>

          <Link
            className="text-link-with-illustration hero-illustration cityscape"
            href="/meeting/agenda-2025"
          >
            <span className="label">View Agenda & Add to Calendar</span>
            {drawCityscapeIllustration({
              height: 1080,
              width: 1902,
            })}
          </Link>

          {Body && <div dangerouslySetInnerHTML={{ __html: Body }} />}
        </div>
        <div className="single-type-cell single-type-cell-hero-media">
          <div className="hero-media-container">
            <Image
              alt={HeroImage.data.attributes.alternativeText || ""}
              className="carousel-item-media"
              data-shape="chip"
              height={HeroImage.data.attributes.height}
              priority={true}
              src={HeroImage.data.attributes.url}
              width={HeroImage.data.attributes.width}
            />
          </div>
        </div>
      </main>
    </div>
  );
};

export default RealTimeAnnualMeetingClient;
